import { useCallback } from "react";
import { useIntercom } from "react-use-intercom";
import type { IntercomProps } from "react-use-intercom";
import { UserContextState } from "@src/lib/contexts/UserContext";
import { useImpersonation } from "@src/features/app/hooks/useImpersonation";

export type OpenIntercom = {
  show: () => void;
  showNewMessage: (prePopulatedContent: string) => void;
};

const useOpenIntercom = (userContext?: UserContextState): OpenIntercom => {
  const {
    boot: bootIntercom,
    show: showIntercom,
    showNewMessage: showIntercomNewMessage,
  } = useIntercom();
  const { isImpersonating } = useImpersonation();

  const bootOutsideProduction = useCallback(() => {
    // Intercom only autoBoots in production builds, so custom entry points need
    // to boot manually before opening the messenger in local/dev environments.
    if (process.env.NODE_ENV !== "production") {
      const bootProps: IntercomProps = {};
      if (userContext?.user && !userContext.isStaff && !isImpersonating) {
        bootProps.userId = userContext.user.id.toString();
        bootProps.email = userContext.user.email || undefined;
        bootProps.name = userContext.user.name!;
        if (userContext.user.org) {
          bootProps.company = {
            companyId: userContext.user.org.id.toString(),
            name: userContext.user.org.name,
          };
        }
      }
      bootIntercom(bootProps);
    }
  }, [bootIntercom, userContext, isImpersonating]);

  const show = useCallback(() => {
    bootOutsideProduction();
    showIntercom();
  }, [bootOutsideProduction, showIntercom]);

  const showNewMessage = useCallback(
    (prePopulatedContent: string) => {
      bootOutsideProduction();
      showIntercomNewMessage(prePopulatedContent);
    },
    [bootOutsideProduction, showIntercomNewMessage],
  );

  return { show, showNewMessage };
};

export default useOpenIntercom;
